/**
 * The write-side refusal: two money models of one Role may not count over
 * different windows.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHAT WAS WRONG, AND WHY IT WAS A WRITE DEFECT RATHER THAN A READ ONE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A money term may hold exactly two windows — `ABSOLUTE` (0) and `ANNUAL` (-1)
 * — and a Role's FIRST money term fixes the one every later term is measured
 * against. `coverage-money.ts` refuses a term that disagrees, by name, and the
 * refusal is correct. It just happens at READ time.
 *
 * So an author could `PUT` a cost model declaring no period beside a revenue
 * model declaring one, and the API answered **200**. The read then also
 * answered 200, with `integrity.status: DEGRADED`, the code
 * `WORKLOAD_MONEY_WRONG_PERIOD_BASIS`, and the revenue term back as `null`. The
 * dashboard renders that — `CoverageIntegrityBanner` fires on any non-OK
 * integrity — so a person at a screen is told. Every other caller of this API
 * is not: the Public API, the CLI, the SDK and any integration get a 200 and a
 * missing term with nothing in the contract to act on.
 *
 * The API was accepting a state it then declined to compute. This module is the
 * refusal moved to the write, where the author is still holding the form.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY IT IS ONE CALLBACK RATHER THAN A RULE EACH SCHEMA RESTATES
 * ══════════════════════════════════════════════════════════════════════════
 *
 * THREE bodies carry the money pair, and only two of them are obvious.
 * `RoleWorkloadInputSchema` and `RoleSystemImpactInputSchema` declare it;
 * `RoleTaskGraduationInputSchema` SPREADS `RoleSystemImpactInputSchema.shape`,
 * and a spread copies the fields and leaves every refinement behind. A door
 * built that way inherits the pair and not the rule, silently.
 *
 * `coverage-money-basis-agreement.test.ts` is what makes a FOURTH door
 * impossible to add quietly: it derives every exported schema in this package
 * whose shape holds both `costFormula` and `revenueFormula`, and requires each
 * one to refuse the disagreeing pair. A new body composing the two fields reds
 * that spec rather than shipping the hole again.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHAT THIS DELIBERATELY DOES NOT REACH
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 🔴 **A body cannot see the Role's OTHER stored models, so this closes the
 * pair inside ONE request and nothing wider.** A Role whose workload is
 * `ABSOLUTE` and whose impact is authored `ANNUAL` in a later request is two
 * clean bodies, and this rule sees neither problem.
 *
 * ✅ **THE CROSS-ROW HALF IS CLOSED, AT THE USE CASE, AND NOT BY MAKING THIS
 * RULE WIDER.** `apps/backend/src/roles/domain/value-objects/role-money-basis-change.ts`
 * reads the Role's stored models and compares two whole readings — the Role as
 * stored against the Role the request would leave — so a write is refused only
 * for leaving MORE money terms declined than it found. The obvious rule, refuse
 * while the stored models disagree, is the one that had to be avoided: it
 * refuses every write that merely COEXISTS with a bad pair, including the
 * author's own repair, and makes an already-degraded Role uneditable.
 *
 * The seam is worth keeping straight. This rule is the one a CLIENT can run —
 * it is a schema, it ships in `@nexus/types`, and a form can apply it as the
 * author types. The cross-row rule needs a read and so exists only on the
 * server. Neither replaces the other.
 *
 * 🔴 **The refusal is the pair, never a single model.** One money model can
 * hold either window and both are legal — `isAdmissibleCoverageRatioPeriodBasis`
 * says so and the read agrees. A lone `ABSOLUTE` cost model is a correct,
 * supported, currently-shipping shape and nothing here may refuse it: the
 * disagreement needs two models, exactly as `coverage-money.ts` states in the
 * comment above the branch this mirrors.
 */
import type { z } from "zod";

import type { CoverageFormula } from "./coverage-formula.schema";
import {
  type CoverageFormulaPeriodBasis,
  coverageFormulaPeriodBasis
} from "./coverage-formula-period-basis";
import { coverageMoneyBasisDisagrees } from "./coverage-money";
import { formatCoveragePeriodBasis } from "./coverage-period.types";

/**
 * The two money models of one authored row, as every carrying body holds them.
 *
 * 🚨 BOTH ARE OPTIONAL HERE AND REQUIRED-AND-NULLABLE ON TWO OF THE THREE
 * BODIES, WHICH IS DELIBERATE. `RoleTaskGraduationInputSchema` makes them
 * OPTIONAL — a graduation authors hours and says nothing about price, so an
 * absent model must mean "leave it alone" rather than "remove it" (NEX-3823).
 * A required field is assignable to an optional one, so this shape accepts all
 * three bodies and the rule below reads an absent model exactly as it reads an
 * absent one that was spelled `null`: there is no pair, so there is nothing to
 * hold in step.
 */
export interface CoverageMoneyPair {
  readonly costFormula?: CoverageFormula | null;
  readonly revenueFormula?: CoverageFormula | null;
}

/**
 * The field a disagreement is reported on.
 *
 * The COST model is the basis-setter — `coverage-ratio.ts` resolves it before
 * the revenue model and says the order is load-bearing rather than alphabetical
 * — so the revenue model is the one being held to a window, and it is the field
 * an author has to change. The message names both, so a client that wants to
 * highlight the other one has what it needs.
 */
const REPORTED_ON = "revenueFormula";

/**
 * Refuse a body whose two money models count over different windows.
 *
 * Written as a bare `superRefine` callback rather than a wrapped schema so the
 * three carrying bodies apply it without changing their own shape: every one of
 * them is read elsewhere through `.shape`, and a wrapper would move that.
 */
export function refuseDisagreeingMoneyPeriodBasis(
  pair: CoverageMoneyPair,
  ctx: z.RefinementCtx
): void {
  const cost = basisOf(pair.costFormula);
  const revenue = basisOf(pair.revenueFormula);

  // Either model absent, or either one structurally basis-less. An absent model
  // is the normal state — a Role may state its cost and never its revenue — and
  // a basis-less one is a model the READ already refuses by name
  // (PERIOD_BASIS_MISMATCH inside the model itself). Neither is this rule's
  // business, and refusing here would be a second opinion delivered at a
  // different layer with a different message.
  if (cost === null || revenue === null) {
    return;
  }

  if (!coverageMoneyBasisDisagrees(revenue, cost)) {
    return;
  }

  ctx.addIssue({
    code: "custom",
    path: [REPORTED_ON],
    message:
      `the revenue model is ${formatCoveragePeriodBasis(revenue)} and the cost model is ` +
      `${formatCoveragePeriodBasis(cost)} — two money models of one Role must count over the ` +
      "same window, or the figures they add up to mean nothing. A model is per year when one of " +
      'its inputs declares "perPeriod" and absolute when none does, so state the period on both ' +
      "models or on neither."
  });
}

/**
 * The basis of one model, or `null` when there is no basis to compare.
 *
 * The THREE `null` causes are deliberately merged HERE and nowhere else — no
 * model was authored, no model was SENT, or the model has no basis of its own:
 * the caller's single early return acts on all of them identically, so keeping
 * them apart past this line would be a distinction with one behaviour. The
 * union arms stay distinct in `coverage-formula-period-basis.ts`, where the
 * parity test reads them, and absent-vs-cleared stays distinct at the WRITE,
 * where it decides whether a stored model survives.
 */
function basisOf(formula: CoverageFormula | null | undefined): number | null {
  if (formula === null || formula === undefined) {
    return null;
  }
  const derived: CoverageFormulaPeriodBasis = coverageFormulaPeriodBasis(formula);
  return derived.kind === "derived" ? derived.basis : null;
}
